import { Accessor, For, JSX, children, createSignal, Show } from 'solid-js';
import { createListTransition } from '@solid-primitives/transition-group';
import { Transition } from './Transition';
import Icon from './Icon';
import { Toggle } from './Toggle';

const [list, setList] = createSignal<ToastOptions[]>([]);

function push(option: ToastOptions) {
  setList((pre) => [option, ...pre]);
}

function pop(option?: ToastOptions) {
  setList((pre) => {
    const clone = [...pre];
    const item = option || clone[clone.length - 1];
    return clone.filter((it) => it !== item);
  });
}

export function ToastProvider() {
  const renderList = children(() => (
    <For each={list()}>
      {(it) => (
        <div class="flex overflow-hidden">
          <div
            onClick={() => {
              pop(it);
              it.onClick?.();
            }}
            class="toast"
          >
            {resolveElement(it.message)}
            {it.duration > 0 && <CountDown duration={it.duration} onEnd={() => pop(it)} />}
          </div>
        </div>
      )}
    </For>
  )) as unknown as Accessor<HTMLDivElement[]>;
  const aneList = createListTransition(renderList, {
    exitMethod: 'keep-index',
    onChange({ added, removed, finishRemoved }) {
      requestAnimationFrame(() => {
        added.forEach((el) => {
          const height = el.clientHeight;
          el.style.height = '0px';
          el.style.transition = 'height .5s var(--ease-out)';
          requestAnimationFrame(() => {
            el.style.height = `${height}px`;
          });
        });
      });
      removed.forEach((el) => {
        el.style.height = '0px';
        // 这里先随便实现一下吧, 严格来说应该监听动画完成，因为不活跃（切换后台）的元素动画会暂停，timeout不会暂停。
        setTimeout(() => {
          finishRemoved([el]);
        }, 550);
      });
    },
  });

  return (
    <Toggle when={aneList().length > 0}>
      <div class="toast-layer">{aneList()}</div>
    </Toggle>
  );
}

function CountDown(props: { duration: number; onEnd: () => void }) {
  return (
    <Transition appear name="circle-countdown" onAfterEnter={() => props.onEnd()}>
      <svg
        class="circle-countdown -mt-0.5 ml-auto h-6 w-6 flex-none"
        viewBox="0 0 32 32"
        style={{ '--duration': `${props.duration}s` }}
      >
        <circle cx="16" cy="16" r="8" />
      </svg>
    </Transition>
  );
}

/**
 * 和pop原理类似，他也会脱离上下文的，不应该在toast发送的组件里面访问上下文。
 * 他应该尽量是纯组件view = f(state)
 * toast 默认兼容Error，会自动调用error样式
 * @param message
 * @param options
 * @returns
 */
export const toast: Toast = ((message, options = {}) => {
  if (message instanceof Error) {
    return toast.error(message.message, options);
  } else {
    const { duration = 3, onClick = () => {}, system = false } = options;
    return push({ duration, onClick, system, message });
  }
}) as Toast;

const iconClass = (className: string) => `size-6 mr-2 -mt-0.5 flex-none ${className}`;
toast.error = ((message, options) => {
  return toast(
    () => (
      <>
        <Icon class={iconClass('text-error')} name="Error" />
        {resolveElement(message)}
      </>
    ),
    options
  );
}) as ToastFn;

toast.success = ((message, options) => {
  return toast(
    () => (
      <>
        <Icon class={iconClass('text-success')} name="Success" />
        {resolveElement(message)}
      </>
    ),
    options
  );
}) as ToastFn;

toast.warning = ((message, options) => {
  return toast(
    () => (
      <>
        <Icon class={iconClass('text-warning')} name="Caution" />
        {resolveElement(message)}
      </>
    ),
    options
  );
}) as ToastFn;

type ToastFn = (message: string | Accessor<JSX.Element>, options?: Partial<Omit<ToastOptions, 'message'>>) => void;

interface Toast {
  (message: string | Error | Accessor<JSX.Element>, options?: Partial<Omit<ToastOptions, 'message'>>): void;
  error: ToastFn;
  success: ToastFn;
  warning: ToastFn;
}

interface ToastOptions {
  message: string | Accessor<JSX.Element>;
  duration: number;
  onClick(): void;
  system: boolean;
}

function resolveElement(el: string | Accessor<JSX.Element>): JSX.Element {
  return typeof el === 'function' ? el() : el;
}
